import moment from 'moment-timezone';

import type { Field, FieldEnumOption, FieldEnumOptions } from '@models';

const USER_TIMEZONE = moment.tz.guess();

export const createEnumOptions = (
  values: FieldEnumOptions,
): FieldEnumOption[] =>
  values.map(item =>
    typeof item === 'string' ? { value: item, label: item } : item,
  );

export const getEnumLabel = (value: string, options: FieldEnumOption[]) => {
  const option = options.find(item => item.value === value);

  return option?.label;
};

export interface FieldFormatterProps {
  field: Field;
  className?: string;
  emptyValue?: string;
}

const FieldFormatter = ({
  field,
  className = '',
  emptyValue = '<Not set>',
}: FieldFormatterProps) => {
  const { data, typeName } = field;
  let displayText = 'Invalid';
  if (typeof data === 'undefined') {
    displayText = emptyValue;
  } else {
    switch (typeName) {
      case 'Boolean':
        displayText = data ? 'True' : 'False';
        break;
      case 'DateTime':
        displayText = data
          ? moment.utc(data).tz(USER_TIMEZONE).fromNow()
          : emptyValue;
        break;
      case 'TimePeriod': {
        const periodObj: Record<string, string> = {
          d: 'Day',
          h: 'Hour',
          w: 'Week',
        };
        const matches = data?.match(/^([1-9]\d*)(w|d|h)$/);
        if (matches) {
          const num = parseInt(matches[1], 10);
          const period = matches[2];
          displayText = `${num} ${periodObj[period]}${num > 1 ? 's' : ''}`;
        } else {
          displayText = emptyValue;
        }
        break;
      }
      case 'EnumList': {
        const { enumValues } = field;

        if (data?.length && enumValues?.length) {
          const enumOptions = createEnumOptions(enumValues);

          displayText = data
            .map(value => getEnumLabel(value, enumOptions))
            .filter(Boolean)
            .join(', ');
        } else {
          displayText = data?.join(', ') || emptyValue;
        }
        break;
      }
      case 'Enum': {
        const { enumValues } = field;

        if (data && enumValues?.length) {
          const enumOptions = createEnumOptions(enumValues);

          displayText = getEnumLabel(data, enumOptions) ?? '';
        } else {
          displayText = data || emptyValue;
        }

        break;
      }
      case 'Int':
      case 'BigInt':
      case 'Float':
        displayText = data === undefined ? emptyValue : String(data);
        break;
      case 'StringList':
        displayText = data === undefined ? emptyValue : data.join(', ');
        break;
      case 'DateRange': {
        if (data === undefined) {
          displayText = emptyValue;
        } else {
          const from = data[0];
          const to = data[1];
          if (!to) {
            displayText = ``;
          }
          if (!to) {
            displayText = `Since ${from}`;
          }
          displayText = `${from}-${to}`;
        }
        break;
      }
      case 'JSON':
        displayText =
          data === undefined ? emptyValue : JSON.stringify(data, null, 2);
        break;
      case 'KeyValue': {
        if (data === undefined || Object.keys(data).length === 0) {
          displayText = emptyValue;
        } else {
          displayText = Object.entries(data)
            .map(([key, value]) => `${key} : ${value}`)
            .join(', ');
        }
        break;
      }

      default:
        // String fields and fallback
        displayText = (data as string | undefined) || emptyValue;
    }
  }
  return <span className={className}>{displayText}</span>;
};

export default FieldFormatter;
